import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Hash, PieChart, Clock as ClockIcon, MousePointerClick, Map as MapIcon, ListChecks } from 'lucide-react';
const TYPES = [{
  id: 'numberline',
  label: 'Getallenlijn',
  icon: Hash,
  color: 'orange'
}, {
  id: 'fraction',
  label: 'Breukentaart',
  icon: PieChart,
  color: 'violet'
}, {
  id: 'clock',
  label: 'Analoge klok',
  icon: ClockIcon,
  color: 'sky'
}, {
  id: 'drag',
  label: 'Drag & drop',
  icon: MousePointerClick,
  color: 'emerald'
}, {
  id: 'map',
  label: 'Topo kaart',
  icon: MapIcon,
  color: 'rose'
}, {
  id: 'mc',
  label: 'Multiple choice',
  icon: ListChecks,
  color: 'amber'
}];
const COLOR_MAP: Record<string, {
  ring: string;
  text: string;
  bg: string;
  solid: string;
}> = {
  orange: {
    ring: 'ring-orange-300',
    text: 'text-orange-700',
    bg: 'bg-orange-50',
    solid: 'bg-orange-500'
  },
  violet: {
    ring: 'ring-violet-300',
    text: 'text-violet-700',
    bg: 'bg-violet-50',
    solid: 'bg-violet-500'
  },
  sky: {
    ring: 'ring-sky-300',
    text: 'text-sky-700',
    bg: 'bg-sky-50',
    solid: 'bg-sky-500'
  },
  emerald: {
    ring: 'ring-emerald-300',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    solid: 'bg-emerald-500'
  },
  rose: {
    ring: 'ring-rose-300',
    text: 'text-rose-700',
    bg: 'bg-rose-50',
    solid: 'bg-rose-500'
  },
  amber: {
    ring: 'ring-amber-300',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    solid: 'bg-amber-500'
  }
};
export function QuestionTypesSection() {
  const [active, setActive] = useState('numberline');
  const activeType = TYPES.find(t => t.id === active)!;
  return <section id="vraagtypes" className="relative py-24 lg:py-32 bg-[#FFF9F2] overflow-hidden" data-id="element-588">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-589">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-590">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 border border-sky-200 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-6" data-id="element-591">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" data-id="element-592" />
            38 vraagtypes · 55 hulpmiddelen · richting 200+
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight" data-id="element-593">
            Elk concept krijgt{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-indigo-500" data-id="element-594">
              z'n eigen vorm.
            </span>
          </h2>
          <p className="text-lg text-slate-600 font-medium max-w-2xl mx-auto" data-id="element-595">
            Geen multiple choice op alles. Een klok wordt een klok. Een breuk
            een taart. Een provincie op de kaart. Tik op een type — proef de
            variatie.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-8" data-id="element-596">
          {TYPES.map(t => {
          const c = COLOR_MAP[t.color];
          const isActive = t.id === active;
          const Icon = t.icon;
          return <motion.button key={t.id} onClick={() => setActive(t.id)} whileHover={{
            y: -2
          }} whileTap={{
            scale: 0.96
          }} className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all ${isActive ? `${c.solid} text-white shadow-lg` : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`} data-id="element-597">
                <Icon className="w-4 h-4" strokeWidth={2.5} data-id="element-598" />
                {t.label}
              </motion.button>;
        })}
        </div>

        {/* Live preview */}
        <motion.div layout className="max-w-3xl mx-auto bg-white rounded-3xl border-4 border-white shadow-2xl overflow-hidden" data-id="element-599">
          <div className="h-9 bg-slate-50 border-b border-slate-100 flex items-center px-4 gap-2" data-id="element-600">
            <div className="flex gap-1.5" data-id="element-601">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-400" data-id="element-602" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-603" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-604" />
            </div>
            <div className="flex-1 text-center text-[10px] font-bold text-slate-400" data-id="element-605">
              {activeType.label}
            </div>
          </div>

          <div className="p-8 min-h-[380px] flex items-center justify-center bg-gradient-to-b from-[#FFF9F2] to-white" data-id="element-606">
            <AnimatePresence mode="wait" data-id="element-607">
              <motion.div key={active} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.3
            }} className="w-full max-w-md" data-id="element-608">
                {active === 'numberline' && <NumberLineDemo data-id="element-609" />}
                {active === 'fraction' && <FractionDemo data-id="element-610" />}
                {active === 'clock' && <ClockDemo data-id="element-611" />}
                {active === 'drag' && <DragDropDemo data-id="element-612" />}
                {active === 'map' && <MapDemo data-id="element-613" />}
                {active === 'mc' && <MultipleChoiceDemo data-id="element-614" />}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        <p className="text-center mt-6 text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-615">
          + 32 andere vraagtypes en 55 hulpmiddelen — op weg naar 200+
        </p>
      </div>
    </section>;
}
function NumberLineDemo() {
  return <div data-id="element-616">
      <div className="text-sm font-bold text-slate-500 mb-4 text-center" data-id="element-617">
        Waar staat <span className="font-black text-slate-800" data-id="element-618">3¾</span> op de
        getallenlijn?
      </div>
      <div className="relative h-16 mb-2" data-id="element-619">
        <div className="absolute top-8 left-0 right-0 h-1 bg-slate-300 rounded-full" data-id="element-620" />
        {[0, 1, 2, 3, 4, 5].map((n, i) => <div key={n} className="absolute top-6 -translate-x-1/2" style={{
        left: `${i / 5 * 100}%`
      }} data-id="element-621">
            <div className="w-0.5 h-5 bg-slate-400 mx-auto" data-id="element-622" />
            <div className="text-[11px] font-bold text-slate-500 mt-1 text-center" data-id="element-623">
              {n}
            </div>
          </div>)}
        <motion.div className="absolute top-3 -translate-x-1/2" initial={{
        left: '50%'
      }} animate={{
        left: '75%'
      }} transition={{
        delay: 0.4,
        type: 'spring',
        stiffness: 200
      }} data-id="element-624">
          <div className="w-5 h-5 rounded-full bg-orange-500 ring-4 ring-orange-200 mx-auto shadow-lg" data-id="element-625" />
          <div className="text-[11px] font-black text-orange-600 mt-1 text-center" data-id="element-626">
            3¾
          </div>
        </motion.div>
      </div>
    </div>;
}
function FractionDemo() {
  return <div data-id="element-627">
      <div className="text-sm font-bold text-slate-500 mb-4 text-center" data-id="element-628">
        Hoeveel is <span className="font-black text-slate-800" data-id="element-629">2/3 + 1/6</span>?
      </div>
      <div className="flex items-center justify-center gap-3" data-id="element-630">
        <FractionPie filled={4} total={6} color="#F97316" data-id="element-631" />
        <span className="text-2xl font-black text-slate-400" data-id="element-632">+</span>
        <FractionPie filled={1} total={6} color="#A78BFA" data-id="element-633" />
        <span className="text-2xl font-black text-slate-400" data-id="element-634">=</span>
        <FractionPie filled={5} total={6} color="#10B981" data-id="element-635" />
      </div>
      <div className="mt-4 text-center text-xs font-bold text-emerald-600" data-id="element-636">
        ⇒ 5/6
      </div>
    </div>;
}
function FractionPie({
  filled,
  total,
  color
}: {
  filled: number;
  total: number;
  color: string;
}) {
  const size = 64;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 3;
  return <svg width={size} height={size} data-id="element-637">
      <circle cx={cx} cy={cy} r={r} fill="#FEF3C7" data-id="element-638" />
      {Array.from({
      length: total
    }).map((_, i) => {
      const startAngle = i * 360 / total - 90;
      const endAngle = (i + 1) * 360 / total - 90;
      const startRad = startAngle * Math.PI / 180;
      const endRad = endAngle * Math.PI / 180;
      const x1 = cx + r * Math.cos(startRad);
      const y1 = cy + r * Math.sin(startRad);
      const x2 = cx + r * Math.cos(endRad);
      const y2 = cy + r * Math.sin(endRad);
      return <motion.path key={i} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`} fill={i < filled ? color : 'transparent'} stroke="#92400E" strokeWidth="1.5" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.1 * i
      }} data-id="element-639" />;
    })}
    </svg>;
}
function ClockDemo() {
  // "Tien voor half 4" = 3:20
  const hours = 3;
  const minutes = 20;
  const hourAngle = hours % 12 * 30 + minutes / 60 * 30; // 100°
  const minuteAngle = minutes * 6; // 120°
  const size = 200;
  return <div data-id="element-640">
      <div className="text-sm font-bold text-slate-500 mb-4 text-center" data-id="element-641">
        Hoe laat is het?{' '}
        <span className="font-black text-slate-800" data-id="element-642">Tien voor half 4</span>
      </div>
      <div className="flex justify-center" data-id="element-643">
        <div className="relative" style={{
        width: size,
        height: size
      }} data-id="element-644">
          {/* Face */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white to-slate-50 shadow-xl border-8 border-slate-100" data-id="element-645" />
          <div className="absolute inset-2 rounded-full border-4 border-slate-200/60" data-id="element-646" />

          {/* Hour numbers */}
          {Array.from({
          length: 12
        }).map((_, i) => {
          const num = i + 1;
          const rotation = num * 30;
          const radius = size / 2 - 30;
          const angleRad = (rotation - 90) * (Math.PI / 180);
          const x = size / 2 + radius * Math.cos(angleRad);
          const y = size / 2 + radius * Math.sin(angleRad);
          return <div key={num} className="absolute flex items-center justify-center font-black text-slate-700" style={{
            width: 28,
            height: 28,
            left: x - 14,
            top: y - 14,
            fontSize: '1.1rem'
          }} data-id="element-647">
                {num}
              </div>;
        })}

          {/* Hour marks */}
          {Array.from({
          length: 12
        }).map((_, i) => <div key={`hm-${i}`} className="absolute bg-slate-400 rounded-full" style={{
          width: 3,
          height: 10,
          left: 'calc(50% - 1.5px)',
          top: 8,
          transformOrigin: `50% ${size / 2 - 8}px`,
          transform: `rotate(${i * 30}deg)`
        }} data-id="element-648" />)}

          {/* Hour hand */}
          <motion.div className="absolute z-10 rounded-full" style={{
          width: 8,
          height: size * 0.28,
          left: 'calc(50% - 4px)',
          bottom: '50%',
          borderRadius: '6px 6px 0 0',
          background: 'linear-gradient(to bottom, #1e293b 0%, #334155 100%)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          transformOrigin: 'center bottom'
        }} initial={{
          rotate: 0
        }} animate={{
          rotate: hourAngle
        }} transition={{
          type: 'spring',
          stiffness: 80,
          damping: 14,
          delay: 0.2
        }} data-id="element-649" />

          {/* Minute hand */}
          <motion.div className="absolute z-20 rounded-full" style={{
          width: 6,
          height: size * 0.38,
          left: 'calc(50% - 3px)',
          bottom: '50%',
          borderRadius: '5px 5px 0 0',
          background: 'linear-gradient(to bottom, #f97316 0%, #ea580c 100%)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          transformOrigin: 'center bottom'
        }} initial={{
          rotate: 0
        }} animate={{
          rotate: minuteAngle
        }} transition={{
          type: 'spring',
          stiffness: 80,
          damping: 14,
          delay: 0.4
        }} data-id="element-650" />

          {/* Center dot */}
          <div className="absolute rounded-full z-30" style={{
          width: 16,
          height: 16,
          left: 'calc(50% - 8px)',
          top: 'calc(50% - 8px)',
          background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
          border: '3px solid white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }} data-id="element-651" />
        </div>
      </div>
    </div>;
}
function DragDropDemo() {
  return <div data-id="element-652">
      <div className="text-sm font-bold text-slate-500 mb-4 text-center" data-id="element-653">
        Sleep de woordsoort naar het juiste woord
      </div>
      <div className="space-y-2" data-id="element-654">
        {[{
        word: 'snel',
        tag: 'BIJWOORD',
        match: true,
        color: 'emerald'
      }, {
        word: 'hond',
        tag: 'ZELFSTANDIG NW.',
        match: true,
        color: 'emerald'
      }, {
        word: 'rennen',
        tag: 'WERKWOORD',
        match: true,
        color: 'orange',
        drop: true
      }].map((row, i) => <motion.div key={i} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: i * 0.15
      }} className={`flex items-center justify-between bg-white border-2 ${row.color === 'emerald' ? 'border-emerald-200' : 'border-orange-300'} rounded-2xl px-4 py-3`} data-id="element-655">
            <span className="text-base font-black text-slate-800" data-id="element-656">
              {row.word}
            </span>
            <motion.div animate={row.drop ? {
          x: [0, -5, 0]
        } : undefined} transition={{
          duration: 0.6,
          repeat: Infinity,
          repeatDelay: 1
        }} className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-lg ${row.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700 ring-2 ring-orange-300'}`} data-id="element-657">
              {row.tag}
            </motion.div>
          </motion.div>)}
      </div>
    </div>;
}
function MapDemo() {
  // 12 Nederlandse provincies — gestileerde geografische posities
  const provinces = [{
    id: 'FR',
    name: 'Friesland',
    d: 'M 30,12 L 56,8 L 58,24 L 50,32 L 32,30 Z',
    active: true
  }, {
    id: 'GR',
    name: 'Groningen',
    d: 'M 56,8 L 78,10 L 82,22 L 70,28 L 58,24 Z'
  }, {
    id: 'DR',
    name: 'Drenthe',
    d: 'M 58,24 L 70,28 L 72,42 L 56,42 L 50,32 Z'
  }, {
    id: 'OV',
    name: 'Overijssel',
    d: 'M 50,32 L 56,42 L 72,42 L 70,56 L 52,54 L 46,44 Z'
  }, {
    id: 'FL',
    name: 'Flevoland',
    d: 'M 38,38 L 46,44 L 52,54 L 42,56 L 34,48 Z'
  }, {
    id: 'GE',
    name: 'Gelderland',
    d: 'M 42,56 L 52,54 L 70,56 L 72,72 L 58,76 L 44,68 Z'
  }, {
    id: 'NH',
    name: 'Noord-Holland',
    d: 'M 28,16 L 32,30 L 38,38 L 34,48 L 26,52 L 20,38 L 22,22 Z'
  }, {
    id: 'UT',
    name: 'Utrecht',
    d: 'M 34,48 L 42,56 L 44,68 L 36,68 L 28,58 Z'
  }, {
    id: 'ZH',
    name: 'Zuid-Holland',
    d: 'M 20,52 L 26,52 L 34,48 L 28,58 L 36,68 L 30,76 L 16,72 Z'
  }, {
    id: 'ZE',
    name: 'Zeeland',
    d: 'M 6,72 L 22,70 L 22,82 L 8,82 Z'
  }, {
    id: 'NB',
    name: 'Noord-Brabant',
    d: 'M 22,72 L 30,76 L 44,68 L 58,76 L 56,86 L 38,90 L 24,86 Z'
  }, {
    id: 'LI',
    name: 'Limburg',
    d: 'M 56,86 L 58,76 L 72,72 L 80,82 L 72,92 L 60,94 Z'
  }];
  return <div data-id="element-658">
      <div className="text-sm font-bold text-slate-500 mb-4 text-center" data-id="element-659">
        Klik op <span className="font-black text-slate-800" data-id="element-660">Friesland</span>
      </div>
      <div className="relative w-full max-w-[280px] mx-auto aspect-square bg-sky-50 rounded-2xl overflow-hidden border-2 border-sky-100" data-id="element-661">
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" data-id="element-662">
          <defs data-id="element-663">
            <pattern id="water-dots" x="0" y="0" width="5" height="5" patternUnits="userSpaceOnUse" data-id="element-664">
              <circle cx="2.5" cy="2.5" r="0.4" fill="#7DD3FC" opacity="0.5" data-id="element-665" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#water-dots)" data-id="element-666" />

          {/* Provinces */}
          {provinces.map((p, i) => <motion.path key={p.id} d={p.d} fill={p.active ? '#F97316' : '#FED7AA'} stroke={p.active ? '#C2410C' : '#FB923C'} strokeWidth="0.6" strokeLinejoin="round" initial={{
          opacity: 0
        }} animate={p.active ? {
          opacity: [0.7, 1, 0.7]
        } : {
          opacity: 1
        }} transition={p.active ? {
          duration: 2,
          repeat: Infinity
        } : {
          delay: 0.05 * i
        }} data-id="element-667" />)}

          {/* Pin on Friesland */}
          <motion.g initial={{
          y: -20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.6,
          type: 'spring',
          stiffness: 200
        }} data-id="element-668">
            <circle cx="44" cy="20" r="3.5" fill="#1E293B" data-id="element-669" />
            <circle cx="44" cy="20" r="1.5" fill="#FCD34D" data-id="element-670" />
          </motion.g>
        </svg>
      </div>
      <div className="mt-3 text-center text-xs font-bold text-emerald-600" data-id="element-671">
        ✓ Goed gevonden
      </div>
    </div>;
}
function MultipleChoiceDemo() {
  return <div data-id="element-672">
      <div className="text-sm font-bold text-slate-500 mb-4 text-center" data-id="element-673">
        Welk woord is een{' '}
        <span className="font-black text-slate-800" data-id="element-674">voltooid deelwoord</span>?
      </div>
      <div className="space-y-2" data-id="element-675">
        {[{
        label: 'lopen',
        correct: false
      }, {
        label: 'gelopen',
        correct: true
      }, {
        label: 'liep',
        correct: false
      }, {
        label: 'lopend',
        correct: false
      }].map((opt, i) => <motion.div key={opt.label} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1 + i * 0.08
      }} className={`flex items-center gap-3 rounded-2xl px-4 py-3 border-2 font-bold ${opt.correct ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-600'}`} data-id="element-676">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black ${opt.correct ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-677">
              {String.fromCharCode(65 + i)}
            </div>
            <span data-id="element-678">{opt.label}</span>
            {opt.correct && <motion.span initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.6 + i * 0.08,
          type: 'spring'
        }} className="ml-auto text-emerald-600" data-id="element-679">
                ✓
              </motion.span>}
          </motion.div>)}
      </div>
    </div>;
}